// 視聴側 WebRTC PeerConnection 管理。answer 側なので Offer を受けて Answer を返す。
// 受信 track は media-elements へ、状態変化は status-ui / viewport へ委譲する。
// signaling 送信 (ICE / answer) は循環参照回避のため注入された sendSignaling を使う。

import { STUN_SERVERS } from "@wincast/shared";
import type { SignalingMessage } from "@wincast/shared";
import { attachAudioTrack, attachVideoForStream } from "./media-elements.js";
import { setStageStatusKey } from "./status-ui.js";
import { scheduleOverlayHide } from "./viewport.js";

let peerConnection: RTCPeerConnection | null = null;
let sendSignaling: (msg: SignalingMessage) => void;

export function initPeerConnection(deps: {
  sendSignaling: (msg: SignalingMessage) => void;
}): void {
  sendSignaling = deps.sendSignaling;
}

export function getPeerConnectionState(): RTCPeerConnectionState | null {
  return peerConnection?.connectionState ?? null;
}

export function closePeerConnection(): void {
  if (peerConnection) {
    peerConnection.close();
    peerConnection = null;
  }
}

export function setupPeerConnection(): void {
  const pc = new RTCPeerConnection({
    iceServers: [{ urls: [...STUN_SERVERS] }],
    bundlePolicy: "max-bundle",
  });
  peerConnection = pc;

  pc.addEventListener("icecandidate", (event) => {
    if (!event.candidate) return;
    sendSignaling({
      type: "ice-candidate",
      candidate: event.candidate.toJSON(),
    });
  });

  pc.addEventListener("track", (event) => {
    if (event.track.kind === "audio") {
      attachAudioTrack(event.track);
      return;
    }
    const [stream] = event.streams;
    if (stream) {
      attachVideoForStream(stream);
    } else {
      const ms = new MediaStream([event.track]);
      attachVideoForStream(ms);
    }
  });

  pc.addEventListener("connectionstatechange", () => {
    switch (pc.connectionState) {
      case "connected":
        setStageStatusKey("stage.connected", "connected");
        scheduleOverlayHide();
        break;
      case "failed":
        setStageStatusKey("stage.connectFailed", "error");
        break;
      case "disconnected":
        setStageStatusKey("stage.tempDisconnect", "error");
        break;
      case "closed":
        setStageStatusKey("stage.closed", "error");
        break;
      default:
        break;
    }
  });
}

export async function onRemoteOffer(sdp: string): Promise<void> {
  if (!peerConnection) return;
  setStageStatusKey("stage.connecting");
  await peerConnection.setRemoteDescription({ type: "offer", sdp });
  const answer = await peerConnection.createAnswer();
  await peerConnection.setLocalDescription(answer);
  sendSignaling({ type: "answer", sdp: answer.sdp ?? "" });
}

export async function onRemoteIce(
  candidate: RTCIceCandidateInit | null | undefined,
): Promise<void> {
  if (!peerConnection || !candidate) return;
  try {
    await peerConnection.addIceCandidate(candidate);
  } catch (err) {
    // err には ICE 候補文字列 (IP を含む) が紛れる可能性があるため、
    // 種別のみログ (CLAUDE.md 11: ログに IP を含めない)
    console.warn(
      "[WinCast viewer] addIceCandidate failed:",
      err instanceof Error ? err.name : "unknown",
    );
  }
}
